import { getInitial, getInitialBackground } from "../lib/utility";
import { httpService } from "./httpService";

export let FEEDS_VAR = {
  "ENABLE_READ_MORE_LINK": true,
  "READ_MORE_LIMIT": {
    "GRID": 180,
    "LIST": 200,
    "TRANSCRIPT": 750
  }
};

export enum FeedType {
  audio = "audio_feed",
  video = "video_feed",
  text = "text_feed",
  social = "social_feed",
}

export enum ApiFeedType {
  audio = "audio",
  video = "video",
  text = "text",
  social = "social",
}

export enum ReviewType {
  DefaultStar = 1,
  TrustPilotStar,
  TripAdvisorStar,
  IMDBStar,
  BookingStar,
  AppsumoTaco,
}

export enum RatingType {
  OutOfFive = 1,
  OutOfTen,
}

export enum TextFeedType {
  thumbs_up = "thumbs_up",
  star = "star"
}

export enum TextFeedResponse {
  Zero = "0",
  One = "1"
}

export const payloadFeedType = {
  audio: "audio-feed",
  video: "video-feed",
  text: "text-feed",
  social: "social-feed"
}

enum MediaType {
  UPLOADED = "Uploaded",
  RECORDED = "Recorded",
  SCREEN_RECORDING = "Screen Recording",
}
interface SocialPlatform {
  icon_full_url: string;
  review_url: string;
  review_type: number;
  name: string;
}

interface FeedFields {
  name: string;
  profile_pic_url: string;
  position: string | null;
  organization_name: string | null;
}

export interface singleFeedData {
  id: number;
  comment: string;
  favourite: 0 | 1;
  created_at: string;
  feed_fields: FeedFields;
  rating_type: number | string;
  rating: number;
  review_at: string;
  response: string;
  review_title: string;
  transcript: string | null;
  social_platform: SocialPlatform;
  static_thumb_url: string;
  file_url: string;
  feed_form?: {
    name: string;
    unique_form_id: number;
    public_url: string;
  }
  social_feed_source?: {
    source_url: string;
  };
  type?: string;
  is_download_enabled?: boolean;
  public_url: string;
  is_video_converted: boolean | null;
  is_audio_converted: boolean | null;
  question_answers: Array<{
    feed_form_question_id: number;
    question_type: number;
    question_text: string;
    default_value: string;
    question_label: string;
    mandatory: 0 | 1;
    answer: string;
    placeholder: string;
  }>;
  extra_details: object | null;

}

export interface ShareFeedData {
  slug: string;
  public_url: string;
  public_display_url: string;
  social_media_links: {
    whatsapp: string;
    facebook: string;
    twitter: string;
    linkedin: string;
  };
  emded_code: string;
}

export interface FeedData {
  id: number;
  comment: string;
  feed_type: FeedType;
  favourite: 0 | 1;
  created_at?: string;
  is_download_enabled?: boolean;
  feed_identifier?: string;
  is_locked: boolean;
  feed_fields: {
    name: string | null;
    profile_pic_url: string | null;
    position: string | null;
    organization_name: string | null;
    consent: boolean | null;
  };
  social_feed?: {
    rating_type: RatingType;
    rating?: number;
    review_at: string;
    created_at: string;
    review_title: string;
    social_platform: {
      icon_full_url: string;
      review_url: string | null;
      review_type: ReviewType;
    };
  };
  text_feed?: {
    rating_type: TextFeedType,
    response: TextFeedResponse
  },
  video_feed?: {
    file_url: string,
    static_thumb_url: string,
    is_video_converted: boolean | null,
    transcript: string | null
  },
  audio_feed?: {
    file_url: string,
    is_audio_converted: boolean | null
    transcript: string | null
  }
}

export interface UpdateFeedPayload {
  favourite: 0 | 1;
}
export interface FeedFiltersData {
  feedTypeFilters: {
    name: string,
    icon: string,
  }[];
  feedFormFilters: {
    id: number,
    name: string,
  }[];
  importedFormFilters: {
    name: string,
    icon: string,
  }[];
}

export interface StarIconsResult {
  filledStars: string;
  emptyStars: string;
  halfStar?: string;
}

interface FormFields {
  feed_form_question_id: number;
  question_type: number;
  question_text: string;
  default_value: string;
  question_label: string;
  mandatory: number;
  answer: any;
  placeholder: string;
}

export const feedsService = {
  list(queryString?: string) {
    return httpService.getRequest<any>(`${queryString ? `feeds${queryString}` : "feeds"}`);
  },

  listFilters() {
    return httpService.getRequest<any>("feeds/filter-options");
  },

  show(feedId: number, feedType: ApiFeedType) {
    return httpService.getRequest<any>(`feeds/${feedType}/${feedId}`);
  },

  showShare(feedId: number, feedType: ApiFeedType) {
    return httpService.getRequest<any>(`feeds/${feedType}/${feedId}/share`);
  },

  update(feedId: number, feedType: ApiFeedType, data: UpdateFeedPayload) {
    return httpService.putRequest(`feeds/${feedType}/${feedId}`, data);
  },

  updateFeedFields(feedId: number, feedType: ApiFeedType, formData: FormData) {
    formData.append("_method", "PUT");
    return httpService.postFormDataRequest(`feeds/${feedType}/${feedId}`, formData);
  },

  delete(feedId: number, feedType: ApiFeedType) {
    return httpService.deleteRequest(`feeds/${feedType}/${feedId}`);
  },

  downloadFile(feedId: number, feedType: ApiFeedType) {
    return httpService.getRequest<any>(`feeds/${feedType}/${feedId}/download`);
  },

  isDisplayFullText(feedText: string, layout: keyof typeof FEEDS_VAR.READ_MORE_LIMIT = 'GRID') {
    return !FEEDS_VAR.ENABLE_READ_MORE_LINK
      || feedText.length < FEEDS_VAR.READ_MORE_LIMIT[layout]
      || feedText.length - FEEDS_VAR.READ_MORE_LIMIT[layout] <= 15;
  },

  handleFavoriteUI(heartIcons: NodeListOf<Element>, isFavorite: boolean) {
    heartIcons.forEach(icon => {
        if (isFavorite) {
          icon.classList.remove("text-gray-500", "dark:text-neutral-400");
          icon.classList.add("text-red-500");
          icon.setAttribute("fill", "currentColor");
          icon.classList.add("scale-150");
          const parentButton = icon.closest("button");
          if (parentButton) {
            parentButton.classList.remove("opacity-0");
          }
          setTimeout(() => {
            icon.classList.remove("scale-150");
          }, 150);
        } else {
          icon.classList.remove("text-red-500");
          icon.classList.add("text-gray-500", "dark:text-neutral-400");
          icon.setAttribute("fill", "none");
          icon.classList.add("scale-50");
          setTimeout(() => {
            icon.classList.remove("scale-50");
          }, 150);
        }
    });
  },

  getTextInputField(field: FormFields) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
              <div class="w-full max-w-[92px] pr-2 shrink-0">
                <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
                  ${field.question_label ? field.question_label : field.question_text.split("_").join(" ")}
                </label>
              </div>
              <div class="grow">
                <div class="relative">
                  <input id="${field.feed_form_question_id}"
                    maxlength="190"
                    input-field-text="${field.question_text}"
                    class="border border-gray-100 dark:border-neutral-700 sm:border-transparent py-1.5 px-2 block w-full rounded-md text-sm hover:border-gray-200 placeholder:text-gray-400 resize-none disabled:opacity-50 disabled:pointer-events-none focus:bg-gray-100 focus:border-transparent focus:ring-transparent dark:bg-neutral-800 dark:hover:border-neutral-700 dark:text-neutral-200 dark:placeholder:text-neutral-500 dark:focus:bg-neutral-700"
                    placeholder="Click and type the ${field.placeholder}"
                    value="${field.answer === null ? "" : field.answer}" />
                </div>
                <div class="input-error mt-1 text-xs text-red-500"></div>
              </div>
            </div>`
  },
  
  getTextareaField(field: FormFields) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
      <div class="w-full max-w-[92px] pr-2 shrink-0">
        <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
          ${field.question_label}
        </label>
      </div>
  
      <div class="grow relative">
        <textarea id="${field.feed_form_question_id}"
          maxlength="190"
          class="border border-gray-100 dark:border-neutral-700 sm:border-transparent py-1.5 px-2 block w-full rounded-md text-sm hover:border-gray-200 placeholder:text-gray-400 resize-none disabled:opacity-50 disabled:pointer-events-none focus:bg-gray-100 focus:border-transparent focus:ring-transparent dark:bg-neutral-800 dark:hover:border-neutral-700 dark:text-neutral-200 dark:placeholder:text-neutral-500 dark:focus:bg-neutral-700"
          placeholder="${field.default_value}">${field.answer === null ? "" : field.answer}</textarea>
      </div>
    </div>`
  },
  
 getFileField(feedData: singleFeedData, field: FormFields, feedType: FeedType) {
  
    const initialBackground = getInitialBackground();
  
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
        <div class="w-full max-w-[92px] pr-2 shrink-0">
          <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
            ${field.question_label ? field.question_label : field.question_text}
          </label>
        </div>
        <div class="grow relative">
          <div class="flex flex-wrap md:flex-nowrap items-center gap-3 sm:gap-4">
            <span class="size-[46px] shrink-0 rounded-lg border border-gray-200 inline-flex justify-center items-center dark:border-neutral-700 overflow-hidden box-border" id="${feedData.id}-feed-avatar-preview">
            ${field.answer ? `<img class="w-full h-auto max-h-[46px] object-contain"
              src="${field.answer}"
              alt="Avatar" />` : feedData.feed_fields.name ? `<span class="inline-flex items-center justify-center size-[46px] text-xl font-semibold leading-none rounded-lg ${initialBackground}">${getInitial(feedData.feed_fields.name)}</span>` : ""}
            </span>
            <div class="grow">
              <div class="flex items-center gap-x-1">
                <input type="file" input-field-text="${field.question_text}" id="${field.feed_form_question_id}" file-input="${feedData.id}-feed-avatar" class="hidden" accept="image/*" />
                <button type="button" id="${feedData.id}-feed-upload-logo-button" class="py-2 px-3 inline-flex items-center gap-x-2 text-xs font-medium whitespace-nowrap rounded-lg border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none focus:outline-none dark:bg-neutral-800 focus:outline-none focus:bg-gray-100 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700">
                  <svg class="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" x2="12" y1="3" y2="15"></line>
                  </svg>
                  <span id="${feedData.id}-feed-upload-avatar-button-text">${field.answer ? "Change photo" : "Upload photo"}</span>
                </button>
              </div>
              <p class="mt-1 text-[10px] text-gray-400 dark:text-neutral-600">Photo size upto 15 MB</p>
            </div>
          </div>
          <p id="${feedType}-file-upload-error" class="mt-1 text-xs text-red-600 dark:text-red-500"></p>
        </div>
      </div>`
  },
  
  getCheckboxField(field: FormFields) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
      <div class="w-full max-w-[92px] pr-2 shrink-0">
        <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
          ${field.question_text}
        </label>
      </div>
      <div class="flex items-start gap-2">
        <input type="checkbox" id="${field.feed_form_question_id}"
          class="h-4 w-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-neutral-800 dark:border-neutral-700 dark:focus:ring-blue-600 dark:ring-offset-neutral-900"
          ${field.answer === "1" ? "checked" : ""} />
        <label for="${field.feed_form_question_id}" class="text-sm cursor-pointer text-gray-500 dark:text-neutral-500 break-words capitalize">
          ${field.question_label}
        </label>
      </div>
    </div>`
  },

  getConsentField(field: FormFields){
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
    <div class="w-full max-w-[92px] pr-2 shrink-0">
      <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
      ${field.question_text}
      </label>
    </div>
    <div class="flex items-center gap-2">
      <span class="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-gray-50 text-gray-500 dark:bg-white/10 dark:text-white">${field.answer === "1" ? "Yes" : "No"}</span>
      <p class="text-sm dark:bg-neutral-800 text-gray-500 dark:text-neutral-500 break-words">
      ${field.question_label}
      </p>
    </div>
  </div>`
  },

  getExtraField(key: string, value: string) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
      <div class="w-full max-w-[92px] pr-2 shrink-0">
        <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
        ${key}
        </label>
      </div>
      <div class="flex items-start gap-2">
        <p class="py-1.5 px-0 block w-full text-gray-800 font-normal text-sm dark:text-neutral-200">${value}</p>
      </div>
    </div>
    <div class="flex flex-col items-end">
    <hr class="border-gray-200 w-full sm:max-w-[calc(100%-92px)] dark:border-neutral-700">
    </div>`
  },

  getMetaReviewForm(formName: string, public_url: string) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
        <div class="w-full max-w-[92px] pr-2 shrink-0">
          <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
            Review Form
          </label>
        </div>
        <div class="grow">
          <div class="relative flex items-center gap-2 py-1.5">
            <p class="px-0 text-gray-800 font-normal text-sm dark:text-neutral-200 break-words">${formName}</p>
            <a href=${public_url} target="_blank" class="shrink-0 hs-tooltip-toggle hs-tooltip size-8 inline-flex justify-center items-center text-gray-800 dark:text-neutral-200 hover:bg-gray-100 rounded-md dark:hover:bg-neutral-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-arrow-out-up-right size-4"><path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"/><path d="m21 3-9 9"/><path d="M15 3h6v6"/></svg>
              <span class="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-md shadow-2xs dark:bg-neutral-700" role="tooltip">
                  Form Preview
              </span>
            </a>
            <a href="/forms" target="_blank" class="shrink-0 hs-tooltip-toggle hs-tooltip size-8 inline-flex justify-center items-center text-gray-800 dark:text-neutral-200 hover:bg-gray-100 rounded-md dark:hover:bg-neutral-900">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clipboard-list size-4"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></svg>
              <span class="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-md shadow-2xs dark:bg-neutral-700" role="tooltip">
                  Form List View
              </span>
            </a>
          </div>
        </div>
      </div>`
  },

  getMetaFeedMethod(type: string) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
        <div class="w-full max-w-[92px] pr-2 shrink-0">
          <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
            Method
          </label>
        </div>
        <div class="grow">
          <div class="relative flex items-center gap-2 py-1.5 flex-wrap">
            <p class="px-0 text-gray-800 font-normal text-sm dark:text-neutral-200 break-words">${type}</p>
            ${(type === MediaType.RECORDED || type === MediaType.SCREEN_RECORDING) ? `<span class="inline-block py-1.5 px-3 rounded-lg text-xs font-normal bg-blue-100 text-blue-800 dark:bg-blue-800/30 dark:text-blue-500">Authorized by Feedspace</span>` : ""}
          </div>
        </div>
      </div>`
  },

  getSocialMeta(key: string, value: string) {
    return `<div class="group w-full flex flex-col sm:flex-row gap-1 sm:gap-2 sm:items-center">
        <div class="w-full max-w-[92px] pr-2 shrink-0">
          <label class="flex items-center gap-x-3 text-xs text-gray-500 dark:text-neutral-500 break-words capitalize">
            ${key}
          </label>
        </div>
        <div class="grow">
          <div class="relative flex items-center gap-2 py-1.5">
            <p class="px-0 text-gray-800 font-normal text-sm dark:text-neutral-200 break-words">${value}</p>
          </div>
        </div>
      </div>`
  }
};